/**
 * Which products each piso de venta uses, and which pisos can be edited together.
 * Pure (unit tested); the API applies the same rules (service/utils/segmentationMatch.js).
 *
 * A piso uses its own exception when it has products, else the client's default. Several pisos
 * can be selected together only when they use exactly the same products (order ignored), so one
 * save never overwrites different lists.
 */

/** Order- and duplicate-insensitive identity of a product list. */
export function segmentationKey(products) {
    return [...new Set((products || []).map((p) => String(p).trim()).filter(Boolean))].sort().join('|');
}

/**
 * Per store id: the products it uses and whether that comes from its own exception.
 * @param {Array<{ _id: string }>} stores
 * @param {Array<{ storeId: string|{ _id: string }, products?: string[] }>} overrides - the client's active exceptions
 * @param {string[]} defaultProducts - the client's segmentation
 * @returns {Map<string, { products: string[], hasOverride: boolean, key: string }>}
 */
export function buildStoreSegmentations(stores, overrides, defaultProducts) {
    const byStore = new Map();
    for (const o of Array.isArray(overrides) ? overrides : []) {
        const storeId = String(o?.storeId?._id || o?.storeId || '');
        if (storeId && Array.isArray(o.products) && o.products.length) byStore.set(storeId, o.products);
    }
    const result = new Map();
    for (const store of stores || []) {
        const id = String(store._id);
        const own = byStore.get(id);
        const products = own ? [...own] : [...(defaultProducts || [])];
        result.set(id, { products, hasOverride: Boolean(own), key: segmentationKey(products) });
    }
    return result;
}

/** The shared segmentation key of a selection, or null when empty or (shouldn't happen) mixed. */
export function selectionKey(storeIds, segmentations) {
    const keys = new Set((storeIds || []).map((id) => segmentations.get(String(id))?.key));
    return keys.size === 1 ? [...keys][0] ?? null : null;
}

/** Whether `storeId` may join `selectedIds`: same products as the stores already selected. */
export function canSelectStore(storeId, selectedIds, segmentations) {
    const info = segmentations.get(String(storeId));
    if (!info) return false;
    if (!selectedIds?.length) return true;
    return selectionKey(selectedIds, segmentations) === info.key;
}

/**
 * The next selection after the picker changed: stores with a different segmentation than the
 * ones kept are dropped. Returns the ids kept and how many were refused.
 */
export function applySelection(nextIds, segmentations) {
    const kept = [];
    let refused = 0;
    for (const id of nextIds || []) {
        if (canSelectStore(id, kept, segmentations)) kept.push(String(id));
        else refused += 1;
    }
    return { ids: kept, refused };
}
